// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import Decimal from 'decimal.js';
import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // Check if body is an instance of FormData
  const isFormData = body instanceof FormData;

  // If headers are not set, we assume that the body should be serialized as transit format.
  // If it's not FormData, determine if the body should be serialized as transit
  const shouldSerializeBody =
    !isFormData && (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : { body };

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || (isFormData ? {} : { 'Content-Type': 'application/transit+json' }), // Do not set Content-Type if it's FormData
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

export const initiateSellTransaction = body => {
  return post('/api/initiate-sell-transaction', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const verifyValidSignUp = body => {
  return post('/api/signup/verify', body);
};

export const confirmSignUp = body => {
  return post('/api/signup/confirm', body);
};

export const notifyAdmin = body => {
  return post('/api/notify-admin', body);
};
export const notifyUser = body => {
  return post('/api/notify-user', body);
};

export const getWeeklyEventActivities = () => {
  return get('/api/weekly-event-activities');
};

export const getLatestSearch = () => {
  return get('/api/latest-search');
};

export const getUserPermissions = userId => {
  return get(`/api/user-permissions?id=${userId}`);
};

export const transactionZapierWebhook = body => {
  return post(`/api/transaction-zapier-webhook`, body);
};

export const postUploadToS3 = file => {
  const formData = new FormData();

  formData.append('file', file);
  const requestOptions = {
    method: 'POST',
    body: formData,
    headers: {
      // Let the browser set the appropriate 'Content-Type' for FormData
      // Content-Type should not be explicitly set here because FormData needs to include boundary
    },
  };
  return request('/api/upload-s3', requestOptions);
};

export const getUserFullName = body => {
  return post(`/api/get-user-full-name`, body);
};

export const postGetFileFromS3 = body => {
  return post(`/api/get-s3`, body);
};
export const getUsersName = body => {
  return post('/api/get-users-name', body);
};
export const totalBudget = body => {
  return post(`/api/get-total-budget`, body);
};
export const updateTxMetadata = body => {
  return post('/api/update-tx-metadata', body);
};

// Accept an application form: unlocks the chat (updates the transaction
// metadata) and emails the customer. Used by both the buyer and seller
// application forms so the logic stays centralized on the backend.
export const acceptApplication = body => {
  return post('/api/accept-application', body);
};

export const checkUserTransaction = body => {
  return post('/api/check-user-transaction', body);
};

export const getTransactionFiles = body => {
  return post('/api/s3/get-transaction-files', body);
};

export const getPosts = body => {
  return post('/api/blog/get-posts', body);
};

export const getPost = body => {
  return post('/api/blog/get-post', body);
};

export const updatePost = body => {
  return post('/api/blog/update-post', body);
};

export const sendDebtData = body => {
  return post('/api/misc/send-debt-data', body);
};

// A Legal Assistance enquiry, emailed to Mishcon de Reya.
export const sendLegalData = body => {
  return post('/api/misc/send-legal-data', body);
};

export const sendSearchDebtData = body => {
  return post('/api/misc/send-search-debt-data', body);
};

export const sendContactForm = body => {
  return post('/api/misc/send-contact-form', body);
};

export const sendTeamAmMessage = body => {
  return post('/api/misc/send-team-am-message', body);
};

/**
 * A GET that also works while the page is being rendered on the server.
 *
 * `request` above uses `window.fetch` and `window.location.origin`, so it can
 * only run in a browser. That is fine for everything else here, which is fired
 * by something the visitor did. The Team page is different: its content has to
 * be in the server-rendered HTML for search engines, so its one fetch has to
 * work in Node too.
 *
 * In Node there is no window, so the base URL comes from the marketplace root
 * and `fetch` from the global one (Node 18 and later, which package.json
 * already requires). Nothing is sent with it, so there are no cookies to
 * forward: this is only for public data.
 */
const getUniversal = path => {
  const inBrowser = typeof window !== 'undefined';
  const baseUrl = inBrowser
    ? apiBaseUrl()
    : apiBaseUrl(process.env.REACT_APP_MARKETPLACE_ROOT_URL);
  const fetchFn = inBrowser ? window.fetch : fetch;

  return fetchFn(`${baseUrl}${path}`, {
    method: methods.GET,
    // Sends the session cookie in the browser, so the server knows whether the
    // reader is signed in and can decide whether to include phone numbers.
    ...(inBrowser ? { credentials: 'include' } : {}),
    headers: { 'Content-Type': 'application/json' },
  }).then(res => {
    if (res.status >= 400) {
      throw new Error(`${path} responded ${res.status}`);
    }
    return res.json();
  });
};

/**
 * The account managers shown on the Team page.
 *
 * Public, so it is also called during server-side rendering. The mobile numbers
 * are only in the response for a signed-in reader — the server decides that, so
 * there is nothing to pass here.
 */
export const getAccountManagers = () => {
  return getUniversal('/api/misc/account-managers');
};

/**
 * The ids of the listings the signed-in member has saved to their Favourites.
 * Resolves to `{ success, listingIds }`. Browser only.
 */
export const getFavoriteListingIds = () => {
  return get('/api/misc/favorite-listings');
};

/** Save a listing to the signed-in member's Favourites. Idempotent. */
export const addFavoriteListing = listingId => {
  return post('/api/misc/favorite-listings/add', { listingId });
};

/** Remove a listing from the signed-in member's Favourites. Idempotent. */
export const removeFavoriteListing = listingId => {
  return post('/api/misc/favorite-listings/remove', { listingId });
};

/** The signed-in member's own account manager. Browser only. */
export const getMyAccountManager = () => {
  return get('/api/misc/my-account-manager');
};

/**
 * Which account manager owns which listing category.
 *
 * Resolves to `{ success, managersByCategory }`, keyed by Sharetribe category
 * id. The whole map comes down at once so the search page can swap its card as
 * the filter changes without another request.
 *
 * Public, and fetched in loadData, so it is also called during server-side
 * rendering — hence getUniversal rather than get.
 */
export const getCategoryAccountManagers = () => {
  return getUniversal('/api/misc/category-account-managers');
};

export const SaveUnfinishedSignup = body => {
  return post('/api/misc/save-unfinished-signup', body);
};

export const getAdminListingAmStats = body => {
  return post('/api/misc/get-admin-listing-am-stats', body);
};

export const getMyHubListings = body => {
  return post('/api/misc/get-my-hub-listings', body);
};

export const sendMsgBlockedWarning = body => {
  return post('/api/misc/send-msg-blocked-warning', body);
};

export const reportUser = body => {
  return post('/api/misc/report-user', body);
};

export const reportMsgBlockIssue = body => {
  return post('/api/misc/report-msg-block-issue', body);
};

export const subscribeNewsletter = body => {
  return post('/api/misc/subscribe-newsletter', body);
};

export const logPaywallView = body => {
  return post('/api/misc/log-paywall-view', body);
};

export const updatePaywallAction = body => {
  return post('/api/misc/update-paywall-action', body);
};

export const initiateRecurringPayment = body => {
  return post('/api/stripe/initiate-recurring-payment', body);
};

export const cancelRecurringPayment = body => {
  return post('/api/stripe/cancel-recurring-payment', body);
};

export const changeSubscription = body => {
  return post('/api/stripe/change-subscription', body);
};

// Turned off with their server routes: unused, and the server did not check
// that the caller owns the subscription. See server/api/payments/index.js.
// export const disableAutoRenewal = body => {
//   return post('/api/stripe/disable-auto-renewal', body);
// };
//
// export const enableAutoRenewal = body => {
//   return post('/api/stripe/enable-auto-renewal', body);
// };

export const validatePayment = body => {
  return post('/api/stripe/validate-payment', body);
};

// Multi-seat ("Multi Desk") subscriptions.
export const claimPendingSeat = body => {
  return post('/api/stripe/claim-pending-seat', body);
};

export const getSeatGroup = body => {
  return post('/api/stripe/get-seat-group', body);
};

export const getSubscriptionPrices = body => {
  return post('/api/stripe/subscription-prices', body);
};

export const createSubscriptionPaymentLink = body => {
  return post('/api/stripe/create-payment-link', body);
};

export const listSubscriptionPaymentLinks = body => {
  return post('/api/stripe/list-payment-links', body);
};

// ---- Deal conversation messages (Parnexus's own store, see server/api/deals) ----

const jsonHeaders = { 'Content-Type': 'application/json' };

// One page of a conversation, newest first: { messages, meta }.
export const getDealMessages = (txId, { page = 1, perPage = 100 } = {}) =>
  request(`/api/deals/${txId}/messages?page=${page}&perPage=${perPage}`, {
    method: methods.GET,
    headers: jsonHeaders,
  });

// Send a message. `clientId` makes a retried request safe: it never posts twice.
export const sendDealMessage = (txId, { content, clientId }) =>
  request(`/api/deals/${txId}/messages`, {
    method: methods.POST,
    headers: jsonHeaders,
    body: JSON.stringify({ content, clientId }),
  });

// ---- The new inbox (server/api/inbox), only while REACT_APP_NEW_INBOX=on ----

const inboxGet = path => request(`/api/inbox${path}`, { method: methods.GET, headers: jsonHeaders });
const inboxPost = (path, body = {}) =>
  request(`/api/inbox${path}`, {
    method: methods.POST,
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
const queryString = params =>
  Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

// The rows of one tab and every count: { rows, counts, meta }.
export const getInbox = ({ tab, stage, chip, page, perPage } = {}) =>
  inboxGet(`?${queryString({ tab, stage, chip, page, perPage })}`);

// The every-minute refresh, in one request: { rows, counts, meta } and, with
// `dealId`, { deal, messages } (only messages from `after` on). `read: 1`
// marks the open deal's new items read.
export const getInboxUpdates = ({ tab, stage, chip, page, dealId, after, read } = {}) =>
  inboxGet(`/updates?${queryString({ tab, stage, chip, page, dealId, after, read })}`);

// ---- The member area (server/api/member-area), only while REACT_APP_MEMBER_AREA=on ----

// The bell: { items: [{ id, type, title, sub, link, createdAt, isNew }], bellOpenedAt }.
export const getMemberNotifications = () =>
  request('/api/member-area/notifications', { method: methods.GET, headers: jsonHeaders });

// The member opened the bell: what is there now is no longer new.
export const markMemberNotificationsOpened = () =>
  request('/api/member-area/notifications/opened', {
    method: methods.POST,
    headers: jsonHeaders,
    body: JSON.stringify({}),
  });

// The "KYC approved" banner was shown once.
export const markKycBannerShown = () =>
  request('/api/member-area/kyc-banner-shown', {
    method: methods.POST,
    headers: jsonHeaders,
    body: JSON.stringify({}),
  });

// Which of the member's passport files are still in S3 (they are deleted 10
// days after upload): { files: [{ key, available }] }.
export const getKycFiles = () =>
  request('/api/member-area/kyc-files', { method: methods.GET, headers: jsonHeaders });

// One passport file, as a Blob (the KYC page downloads it).
export const downloadKycFile = key =>
  window
    .fetch(`${apiBaseUrl()}/api/member-area/kyc-files/download?${queryString({ key })}`, {
      credentials: 'include',
    })
    .then(res => {
      if (!res.ok) throw Object.assign(new Error('kyc-file-download-failed'), { status: res.status });
      return res.blob();
    });

// Only the counts (the navigation badge): { tabs, total, stages, chips }.
export const getInboxCounts = () => inboxGet('/counts');

// Start a deal from an application form: { deal }.
export const createInboxDeal = ({ listingId, form, data }) =>
  inboxPost('/deals', { listingId, form, data });

// One deal as the viewer sees it: { deal }.
export const getInboxDeal = dealId => inboxGet(`/deals/${encodeURIComponent(dealId)}`);

// A page of messages and event cards, newest first: { messages, meta }.
export const getInboxMessages = (dealId, { page = 1, perPage = 100 } = {}) =>
  inboxGet(`/deals/${encodeURIComponent(dealId)}/messages?${queryString({ page, perPage })}`);

// Send a message: { message }. `clientId` makes a retry safe.
export const sendInboxMessage = (dealId, { content, clientId }) =>
  inboxPost(`/deals/${encodeURIComponent(dealId)}/messages`, { content, clientId });

// One of the deal actions (accept-application, request-introduction, ...): { deal }.
export const inboxDealAction = (dealId, action) =>
  inboxPost(`/deals/${encodeURIComponent(dealId)}/actions/${encodeURIComponent(action)}`);

// The member's own things. Each answers with the deal: { deal }.
export const starInboxDeal = (dealId, starred) =>
  inboxPost(`/deals/${encodeURIComponent(dealId)}/star`, { starred });
export const readInboxDeal = dealId => inboxPost(`/deals/${encodeURIComponent(dealId)}/read`);
export const unreadInboxDeal = dealId => inboxPost(`/deals/${encodeURIComponent(dealId)}/unread`);
export const hideInboxDeal = dealId => inboxPost(`/deals/${encodeURIComponent(dealId)}/hide`);
export const ackInboxDeal = (dealId, kind) =>
  inboxPost(`/deals/${encodeURIComponent(dealId)}/ack`, { kind });
